import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { startPresenceHeartbeat, stopPresenceHeartbeat } from '../lib/presence';
import { getAdminSessionResult } from '../api/auth';
import { requestApiJson } from '../api/client';

const AuthContext = createContext();

const TRANSIENT_SESSION_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

const isTransientSessionStatus = (status) =>
  TRANSIENT_SESSION_STATUSES.has(Number(status)) || Number(status) >= 500;

const SESSION_UNAVAILABLE_BANNER_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10000,
  padding: '8px 16px',
  background: 'var(--color-warning)',
  color: 'var(--color-text-strong)',
  borderBottom: '1px solid var(--color-warning-deep)',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: 1.5,
  textAlign: 'center',
  boxShadow: 'var(--shadow-sm)',
};

export const SessionUnavailableBanner = () => (
  <div role="status" aria-live="polite" style={SESSION_UNAVAILABLE_BANNER_STYLE}>
    通信が混み合っています。現在の画面を維持したまま再接続を確認しています。
  </div>
);

const hasNativeAdminAuthBridge = () => {
  if (typeof window === 'undefined') return false;
  const bridge = window.__CASTICART_NATIVE_AUTH__;
  const authorization = String(
    bridge?.authorizationHeader || bridge?.authorization || bridge?.Authorization || ''
  ).trim();
  return /^Bearer\s+\S+/i.test(authorization);
};

const getNativeAdminAuthBridge = () => {
  if (typeof window === 'undefined') return null;
  const bridge = window.__CASTICART_NATIVE_AUTH__;
  if (!bridge || typeof bridge !== 'object') return null;
  const authorization = String(
    bridge.authorizationHeader || bridge.authorization || bridge.Authorization || ''
  ).trim();
  if (!/^Bearer\s+\S+/i.test(authorization)) return null;
  return {
    authorization,
    client: String(bridge.client || 'native').trim(),
    app: String(bridge.app || 'casticart_admin_mac').trim(),
    platform: String(bridge.platform || 'macos').trim(),
    deviceId: String(bridge.deviceId || bridge.deviceID || '').trim(),
  };
};

const isLikelyNativeWebView = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = String(window.navigator?.userAgent || '');
  return Boolean(
    window.__CASTICART_NATIVE_AUTH__ ||
    window.webkit?.messageHandlers?.casticartStageRoute ||
    /Casticart(?:Admin)?Mac/i.test(userAgent)
  );
};

const postNativeStageAuthDebug = (payload) => {
  if (typeof window === 'undefined') return;
  try {
    window.webkit?.messageHandlers?.casticartStageRoute?.postMessage({
      type: 'native-auth-debug',
      ...payload,
    });
  } catch (_) {
    // macOS WebView の診断専用。通常ブラウザでは何もしない。
  }
};

const waitForNativeAdminAuthBridge = async () => {
  if (typeof window === 'undefined') return false;
  if (hasNativeAdminAuthBridge()) return true;
  if (!isLikelyNativeWebView()) return false;
  for (let index = 0; index < 20; index += 1) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    if (hasNativeAdminAuthBridge()) return true;
  }
  postNativeStageAuthDebug({
    phase: 'bridge-timeout',
    hasBridge: Boolean(window.__CASTICART_NATIVE_AUTH__),
    hasAuthorization: hasNativeAdminAuthBridge(),
  });
  return false;
};

const resolveNativeApiOrigin = () => {
  const configured = String(process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  if (configured) return configured;
  if (typeof window === 'undefined') return '';
  const { protocol, hostname, port, origin } = window.location;
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '3001') {
    return `${protocol}//${hostname}:5001`;
  }
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '3000') {
    return `${protocol}//${hostname}:5000`;
  }
  return origin;
};

const isActiveAdminSessionPayload = (data) =>
  Boolean(data && typeof data === 'object' && (data.isLoggedIn || data.userId || data.id));

const nativeSessionTargetLabel = (url) => {
  if (typeof window === 'undefined') return String(url || '');
  try {
    const resolved = new URL(url, window.location.href);
    if (resolved.origin === window.location.origin) return 'same-origin';
    return resolved.origin;
  } catch (_) {
    return String(url || '');
  }
};

const describeNativeSessionPayload = (data) => {
  if (!data || typeof data !== 'object') {
    return {
      active: false,
      hasPayload: false,
      hasUserId: false,
      userType: null,
      role: null,
    };
  }
  return {
    active: isActiveAdminSessionPayload(data),
    hasPayload: true,
    isLoggedIn: data.isLoggedIn === true,
    hasUserId: Boolean(data.userId || data.id),
    userType: data.userType || data.currentRole || null,
    role: data.role || data.adminRole || null,
  };
};

const fetchNativeAdminSession = async () => {
  const bridge = getNativeAdminAuthBridge();
  if (!bridge) return null;
  postNativeStageAuthDebug({
    phase: 'session-fetch-start',
    hasBridge: true,
    hasAuthorization: true,
    apiOrigin: resolveNativeApiOrigin() || null,
  });
  const apiOrigin = resolveNativeApiOrigin();
  const headers = new Headers({
    Accept: 'application/json',
    Authorization: bridge.authorization,
  });
  if (bridge.client) headers.set('x-casticart-client', bridge.client);
  if (bridge.app) headers.set('x-casticart-app', bridge.app);
  if (bridge.platform) headers.set('x-casticart-platform', bridge.platform);
  if (bridge.deviceId) headers.set('x-casticart-device-id', bridge.deviceId);

  const candidates = ['/api/session/admin'];
  if (apiOrigin) {
    candidates.push(`${apiOrigin}/api/session/admin`);
  }

  for (const url of [...new Set(candidates)]) {
    try {
      const { data, response } = await requestApiJson(url, {
        method: 'GET',
        cache: 'no-store',
        headers,
      });
      if (!response.ok) {
        postNativeStageAuthDebug({
          phase: 'session-fetch-response',
          target: nativeSessionTargetLabel(url),
          status: response.status,
          ok: false,
          active: false,
        });
        continue;
      }
      const payload = describeNativeSessionPayload(data);
      postNativeStageAuthDebug({
        phase: 'session-fetch-response',
        target: nativeSessionTargetLabel(url),
        status: response.status,
        ok: true,
        ...payload,
      });
      if (payload.active) return data;
    } catch (error) {
      postNativeStageAuthDebug({
        phase: 'session-fetch-error',
        target: nativeSessionTargetLabel(url),
        message: String(error?.message || error || 'unknown').slice(0, 160),
      });
      if (process.env.NODE_ENV === 'development') {
        console.warn('Native admin session fetch failed:', url, error?.message || error);
      }
    }
  }
  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const normalizeSession = (data) => {
    if (!data || typeof data !== 'object') return null;
    const permissions = Array.isArray(data.permissions)
      ? data.permissions
      : data.permissions
        ? [data.permissions]
        : [];
    const role =
      data.role ||
      data.adminRole ||
      (permissions.includes('admin') || permissions.includes('*') ? 'admin' : null);
    const isLoggedIn = Boolean(data.isLoggedIn || data.userId || data.id);
    return {
      ...data,
      isLoggedIn,
      userType: data.userType || 'admin',
      role,
      permissions,
      permission_entries: Array.isArray(data.permission_entries) ? data.permission_entries : [],
    };
  };

  const fetchSession = useCallback(async () => {
    try {
      await waitForNativeAdminAuthBridge();
      const nativeBridgeAvailable = hasNativeAdminAuthBridge();
      postNativeStageAuthDebug({
        phase: 'fetch-session',
        hasBridge: Boolean(typeof window !== 'undefined' && window.__CASTICART_NATIVE_AUTH__),
        hasAuthorization: nativeBridgeAvailable,
      });

      const evaluateSessionCandidate = (raw, source) => {
        const data = normalizeSession(raw);
        const hasActiveSession = Boolean(data && data.isLoggedIn);
        postNativeStageAuthDebug({
          phase: 'fetch-session-candidate',
          source,
          active: hasActiveSession,
          hasPayload: Boolean(raw && typeof raw === 'object'),
          hasUserId: Boolean(data?.userId || data?.id),
          isLoggedIn: data?.isLoggedIn === true,
          userType: data?.userType || null,
          role: data?.role || null,
        });
        return hasActiveSession ? data : null;
      };

      const runSessionCandidate = async (source, loader) => {
        try {
          const result = await loader();
          if (result && typeof result === 'object' && 'ok' in result && 'status' in result) {
            if (!result.ok) {
              postNativeStageAuthDebug({
                phase: 'fetch-session-candidate',
                source,
                active: false,
                status: result.status,
                unavailable: isTransientSessionStatus(result.status),
              });
              return {
                data: null,
                unavailable: isTransientSessionStatus(result.status),
              };
            }
            return {
              data: evaluateSessionCandidate(result.data, source),
              unavailable: false,
            };
          }
          return {
            data: evaluateSessionCandidate(result, source),
            unavailable: false,
          };
        } catch (error) {
          postNativeStageAuthDebug({
            phase: 'fetch-session-candidate-error',
            source,
            message: String(error?.message || error || 'unknown').slice(0, 160),
          });
          if (process.env.NODE_ENV === 'development') {
            console.warn('Admin session candidate failed:', source, error?.message || error);
          }
          return { data: null, unavailable: true };
        }
      };

      let sawSessionUnavailable = false;
      let candidate = await runSessionCandidate('admin-session', getAdminSessionResult);
      sawSessionUnavailable = sawSessionUnavailable || candidate.unavailable;
      let data = candidate.data;
      if (!data && nativeBridgeAvailable) {
        candidate = await runSessionCandidate('native-direct', fetchNativeAdminSession);
        sawSessionUnavailable = sawSessionUnavailable || candidate.unavailable;
        data = candidate.data;
      }
      if (!data && nativeBridgeAvailable) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        candidate = await runSessionCandidate('admin-session-retry', getAdminSessionResult);
        sawSessionUnavailable = sawSessionUnavailable || candidate.unavailable;
        data = candidate.data;
      }
      if (!data && nativeBridgeAvailable) {
        candidate = await runSessionCandidate('native-direct-retry', fetchNativeAdminSession);
        sawSessionUnavailable = sawSessionUnavailable || candidate.unavailable;
        data = candidate.data;
      }
      const hasActiveSession = Boolean(data && data.isLoggedIn);
      postNativeStageAuthDebug({
        phase: 'fetch-session-final',
        active: hasActiveSession,
        hasAuthorization: nativeBridgeAvailable,
        unavailable: sawSessionUnavailable,
      });

      if (!hasActiveSession) {
        if (sawSessionUnavailable) {
          setSessionUnavailable(true);
          if (userRef.current) {
            return userRef.current;
          }
          setUser(null);
          return null;
        }
        setUser(null);
        setSessionUnavailable(false);
        return null;
      }

      setUser(data);
      setSessionUnavailable(false);
      return data;
    } catch (error) {
      console.warn('Error checking session (preserving current page when possible):', error?.message || error);
      setSessionUnavailable(true);
      if (userRef.current) {
        return userRef.current;
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await fetchSession();
      if (active) setChecking(false);
    })();
    return () => { active = false; };
  }, [fetchSession]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let refreshTimer = null;
    const refreshIfVisible = () => {
      if (document.visibilityState === 'hidden') return;
      fetchSession();
    };
    const schedulePeriodicRefresh = () => {
      if (refreshTimer) clearInterval(refreshTimer);
      refreshTimer = setInterval(() => {
        refreshIfVisible();
      }, 60 * 1000);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchSession();
      }
    };
    const handleFocus = () => {
      refreshIfVisible();
    };
    const handlePermissionRefresh = () => {
      fetchSession();
    };

    schedulePeriodicRefresh();
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('casticart:admin-session-refresh', handlePermissionRefresh);

    return () => {
      if (refreshTimer) clearInterval(refreshTimer);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('casticart:admin-session-refresh', handlePermissionRefresh);
    };
  }, [fetchSession]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleLogout = (event) => {
      const role = event?.detail?.role;
      if (!role || role === 'admin') {
        setUser(null);
        setSessionUnavailable(false);
      }
    };
    window.addEventListener('casticart:logout', handleLogout);
    return () => window.removeEventListener('casticart:logout', handleLogout);
  }, []);

  // ログイン中のみ在席マネージャを稼働（無操作5分/背景でオフライン化）。
  const isAuthenticated = Boolean(user);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    startPresenceHeartbeat();
    return () => stopPresenceHeartbeat();
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ user, fetchSession, checking, sessionUnavailable }}>
      {sessionUnavailable && user ? <SessionUnavailableBanner /> : null}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
